import { Head } from "@inertiajs/react";
import { useEffect, useMemo, useState } from "react";
import { salonJobResolvedVideoUrl, salonJobResolvedPosterUrl, salonjobsArticleImageUrl } from "@/lib/_url";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { VideoHero, VideoModal } from "./_video";
import { salonjobsIntroLines } from "./_intro";
import { salonjobsFormatGraphValue } from "./_graph-value";
import { HairbookInstagramEmbed, HairbookKnowMoreEmbed, HairbookStaffEmbed, HairbookStylesEmbed } from "./_hairbook-embeds";
import { salonjobsLineOfficialUrl } from "./_line-cta";
import { salonjobsApplicationPageUrl } from "./_job-url";
import {
  salonjobsContentNavForInterview,
  salonjobsInterviewVideos,
} from "./_interview-video";

import type {
  Company, ConceptArticle, ContentNavItem, Customers, Graph, HeroVideo, Job,
  ProcessArticle, Salon, SalonjobsShowPayload, SalonjobsShowProps, Settings,
  VideoItem, VisitArticle, WorkstyleArticle,
} from "./_props";

// =====================================================================================
// Page
// =====================================================================================

// Missing or hidden salons yield {}. Keep hooks in the page with a complete SQL payload.
export default function SalonjobsStore(props: SalonjobsShowProps) {
  if (!props.salon) return <div className="min-h-screen flex items-center justify-center text-black/40">Salon not found.</div>;
  return <SalonjobsStorePage key={JSON.stringify([props.salon.id, props.selectedJobId])} {...props} />;
}

function SalonjobsStorePage(props: SalonjobsShowPayload) {
  const {
    salon, company, settings, sectionTitles, heroVideo, heroImage, heroImageUpdatedAt, about, videos, jobs, articles, graphs,
    customers, contentNav, meta, selectedJobId,
  } = props;
  const interviewVideos = salonjobsInterviewVideos(videos);
  const hasInterviewVideo = interviewVideos.length > 0;
  const [activeVideo, setActiveVideo] = useState<VideoItem | null>(null);
  const [applyingJob, setApplyingJob] = useState<Job | null>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const now = new Date(Date.now() + 9 * 60 * 60 * 1000);
  // Selected-job eligibility is owned by the server.
  // Keep the existing queryless presentation, including support buttons.
  const activeJobs = selectedJobId ? jobs : jobs.filter((j) => !j.expiredOn || new Date(j.expiredOn) >= now);
  const applyJobs = selectedJobId ? activeJobs : activeJobs.filter((j) => j.salary !== null);
  const supportJobs = selectedJobId ? [] : activeJobs.filter((j) => j.salary === null);

  return (
    <>
      <Head>
        <meta head-key="keywords" name="keywords" content={meta.keywords} />
      </Head>

      <div className="salonjobs-store bg-[#fafaf7] text-[#0a0a0a] min-h-screen antialiased">
        <Header salon={salon} scrolled={scrolled} />
        <Hero
          salon={salon}
          heroVideo={heroVideo}
          heroImage={heroImage}
          heroImageUpdatedAt={heroImageUpdatedAt}
          contentNav={salonjobsContentNavForInterview(contentNav, hasInterviewVideo)}
          hasInterviewVideo={hasInterviewVideo}
        />
        <Intro about={about} topCatch={salon.topCatch} />
        <Jobs jobs={applyJobs} support={supportJobs} onApply={setApplyingJob} />
        <StyleShowcase salon={salon} />
        <Instagram salon={salon} />
        <Line salon={salon} />
        <Workstyle title={sectionTitles.workstyle} items={articles.workstyle} />
        <Concept title={sectionTitles.concept} items={articles.concept} />
        <Interview title={sectionTitles.interview} salonName={props.salon.name} videos={interviewVideos} onPlay={setActiveVideo} />
        <Data title={sectionTitles.graph} graphs={graphs} />
        {customers && <CustomerSection title={sectionTitles.customers} customers={customers} />}
        <Visit title={sectionTitles.visit} items={articles.visit} />
        <Staff salon={salon} />
        <Process title={sectionTitles.process} items={articles.process} />
        <ApplyCta salon={salon} settings={settings} />
        <RelatedLinks salon={salon} />
        <Footer salon={salon} company={company} />

        {activeVideo && (
          <VideoModal
            video={{
              videoUrl: salonJobResolvedVideoUrl(activeVideo),
              posterUrl: salonJobResolvedPosterUrl(activeVideo),
              categoryLabel: activeVideo.categoryLabel ?? "",
              title: activeVideo.title ?? "",
            }}
            onClose={() => setActiveVideo(null)}
          />
        )}
        {applyingJob && (
          <div
            data-controller="tracking"
            data-track-on="connect"
            data-name="application_form_opened"
            data-job-id={applyingJob.id}
            data-job-position={applyingJob.position}
            data-salon-id={String(salon.id)}
            data-employment-category={applyingJob.employmentCategoryLabel ?? ""}
          >
            <ApplicationFormModal
              job={applyingJob}
              salon={salon}
              onClose={() => setApplyingJob(null)}
            />
          </div>
        )}
      </div>
    </>
  );
}

// =====================================================================================
// Header
// =====================================================================================

function Header({ salon, scrolled }: { salon: Salon; scrolled: boolean }) {
  // Always visible (white bg + dark text) since the new Hero starts with a white area
  // — `scrolled` adds a subtle border + heavier blur once the user starts scrolling.
  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 bg-white/85 backdrop-blur-xl text-[#0a0a0a] transition-shadow duration-300 ${
        scrolled ? "border-b border-black/5 shadow-[0_1px_0_rgba(0,0,0,0.02)]" : "border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 h-14 sm:h-16 flex items-center justify-between gap-3">
        <div className="flex items-baseline gap-2 sm:gap-3 min-w-0 flex-1">
          <span className="text-[10px] tracking-[0.3em] uppercase text-black/50 shrink-0 hidden sm:inline">Recruit</span>
          <span className="font-serif text-sm sm:text-base lg:text-lg tracking-tight truncate">
            {salon.name.split("【")[0].trim()}
          </span>
        </div>
        <a
          href="#apply"
          data-controller="tracking"
          data-action="click->tracking#track"
          data-name="job_requirements_clicked"
          data-salon-id={String(salon.id)}
          data-location="header"
          className="shrink-0 inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.25em] uppercase px-3 sm:px-4 h-8 sm:h-9 rounded-full border border-black/20 hover:bg-black hover:text-white transition"
        >
          Apply
          <span aria-hidden>→</span>
        </a>
      </div>
    </header>
  );
}

// =====================================================================================
// Hero — full bleed video
// =====================================================================================

function Hero({
  salon, heroVideo, heroImage, heroImageUpdatedAt, contentNav, hasInterviewVideo,
}: {
  salon: Salon;
  heroVideo?: HeroVideo;
  heroImage: string | null;
  heroImageUpdatedAt?: string | null;
  contentNav: ContentNavItem[];
  hasInterviewVideo: boolean;
}) {
  const [heroImageMissing, setHeroImageMissing] = useState(false);
  const heroImageUrl = heroImage ? salonjobsArticleImageUrl(heroImage, "gallery", heroImageUpdatedAt) : null;
  const hasHeroMedia = Boolean(heroVideo?.id || (heroImageUrl && !heroImageMissing));

  const nowHiringBadge = (
    <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-10">
      <span className="inline-flex items-center gap-2 px-3 h-7 sm:h-8 bg-black/60 backdrop-blur text-white text-[10px] tracking-[0.3em] uppercase rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
        Now Hiring
      </span>
    </div>
  );

  return (
    <section className="bg-white pt-14 sm:pt-16">
      {/* === Stage 1: Video or Image === */}
      {hasHeroMedia && (
        heroVideo?.id ? (
          <VideoHero src={salonJobResolvedVideoUrl(heroVideo)} poster={salonJobResolvedPosterUrl(heroVideo)}>
            {nowHiringBadge}
          </VideoHero>
        ) : (
          <div className="relative w-full bg-black overflow-hidden aspect-[16/9] max-h-[78vh]">
            <OptionalArticleImage
              articleId={heroImage}
              imageUpdatedAt={heroImageUpdatedAt}
              src={heroImageUrl}
              alt={salon.name}
              onMissing={() => setHeroImageMissing(true)}
              className="absolute inset-0 w-full h-full object-cover"
            />
            {nowHiringBadge}
          </div>
        )
      )}

      {/* === Stage 2: Text === */}
      <div className="px-5 sm:px-6 lg:px-12 pt-10 sm:pt-14 lg:pt-20 pb-6 sm:pb-10">
        <div className="max-w-[1100px] mx-auto">
          <div className="text-[10px] sm:text-[11px] tracking-[0.3em] uppercase text-black/50 mb-3 sm:mb-4 flex items-center gap-2 flex-wrap">
            <span>Recruit</span>
            <span className="opacity-40">·</span>
            <span>{salon.access}</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl leading-[1.15] tracking-tight [word-break:keep-all] [overflow-wrap:break-word] [text-wrap:pretty]">
            {salon.name.split("【")[0].trim()}
          </h1>
          <p className="mt-4 sm:mt-6 text-sm sm:text-base lg:text-lg text-black/70 max-w-2xl font-light leading-relaxed [word-break:keep-all] [overflow-wrap:break-word] [text-wrap:pretty]">
            {salon.tagline}
          </p>
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <a
              href="#apply"
              data-controller="tracking"
              data-action="click->tracking#track"
              data-name="job_requirements_clicked"
              data-salon-id={String(salon.id)}
              data-location="hero"
              className="inline-flex items-center justify-center gap-2 px-7 h-12 rounded-full bg-black text-white text-sm tracking-wide hover:bg-black/85 transition"
            >
              募集要項を見る
              <span aria-hidden>→</span>
            </a>
            {hasInterviewVideo && (
              <a
                href="#interview"
                className="inline-flex items-center justify-center gap-2 px-7 h-12 rounded-full border border-black/15 text-black text-sm tracking-wide hover:bg-black hover:text-white transition"
              >
                <span>▶</span>
                インタビュー動画を見る
              </a>
            )}
          </div>
        </div>
      </div>

      {/* === Stage 3: Content nav chips (table of contents) === */}
      <ContentChips contentNav={contentNav} />
    </section>
  );
}

const CHIP_ICON: Record<ContentNavItem["icon"], string> = {
  video: "▶",
  briefcase: "■",
  users: "○",
  scissors: "✂",
  heart: "♡",
  instagram: "◎",
  chart: "▦",
};

function ContentChips({ contentNav }: { contentNav: ContentNavItem[] }) {
  return (
    <nav
      aria-label="Page contents"
      className="px-5 sm:px-6 lg:px-12 pb-12 sm:pb-16 lg:pb-20 border-b border-black/5"
    >
      <div className="max-w-[1100px] mx-auto">
        <div className="text-[10px] tracking-[0.4em] uppercase text-black/40 mb-4">
          Contents
        </div>
        <ul className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 -mx-5 px-5 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {contentNav.map((c) => (
            <li key={c.label} className="shrink-0">
              <a
                href={c.href}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-black/[0.04] hover:bg-black hover:text-white text-sm transition group"
              >
                <span className="text-xs opacity-60 group-hover:opacity-100 transition">{CHIP_ICON[c.icon]}</span>
                <span className="font-medium tracking-tight">{c.label}</span>
                {c.count && (
                  <span className="text-[11px] tabular-nums text-black/40 group-hover:text-white/60 transition">{c.count}</span>
                )}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}


// =====================================================================================
// Intro
// =====================================================================================
 
function Intro({ about, topCatch }: { about?: string | null; topCatch: string | null }) {
  // Japanese-aware text rendering:
  //  - [word-break:keep-all] prevents breaking inside bracketed phrases like 【透明感カラー】
  //  - [text-wrap:pretty] balances line lengths
  // First line is the catchphrase (large), subsequent lines are description (smaller)
  const lines = salonjobsIntroLines(about, topCatch);
  if (lines.length === 0) return null;

  return (
    <section className="py-16 sm:py-24 lg:py-32 px-5 sm:px-6 lg:px-12">
      <div className="max-w-3xl mx-auto text-center">
        <SectionLabel>About</SectionLabel>
        <div className="mt-6 sm:mt-8 space-y-3 sm:space-y-4 [word-break:keep-all] [overflow-wrap:break-word] [text-wrap:pretty]">
          {lines.map((line, i) => (
            <p
              key={i}
              className={
                i === 0
                  ? "font-serif text-xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.5] sm:leading-[1.4]"
                  : "font-serif text-base sm:text-xl lg:text-2xl tracking-tight leading-[1.7] sm:leading-[1.5] text-black/70"
              }
            >
              {line}
            </p>
          ))}
        </div>
        <div className="mt-8 sm:mt-10 inline-block w-12 h-px bg-black/30" />
      </div>
    </section>
  );
}

// =====================================================================================
// Interview — staff video stories on dark background
// =====================================================================================

function Interview({
  title,
  salonName,
  videos,
  onPlay,
}: {
  title: string;
  salonName: string;
  videos: VideoItem[];
  onPlay: (v: VideoItem) => void;
}) {
  if (videos.length === 0) return null;

  // Parse "店長 萩原 直人" into role + name
  const parseStaff = (videoTitle: string) => {
    const parts = videoTitle.trim().split(/\s+/);
    if (parts.length >= 2) {
      return { role: parts[0], name: parts.slice(1).join(" ") };
    }
    return { role: "", name: videoTitle };
  };

  return (
    <section id="interview" className="py-16 sm:py-24 lg:py-32 px-5 sm:px-6 lg:px-12 bg-[#0a0a0a] text-white">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <SectionLabel light>Interview</SectionLabel>
          <h2 className="font-serif text-2xl sm:text-4xl lg:text-5xl mt-3 sm:mt-4 tracking-tight">{title}</h2>
          <p className="text-white/60 text-xs sm:text-sm mt-3 sm:mt-4 max-w-md mx-auto font-light leading-relaxed">
            スタッフ自身の言葉で語る、{salonName}での働き方。
          </p>
        </div>

        <div className={`grid gap-4 sm:gap-6 max-w-[960px] mx-auto ${videos.length === 1 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`}>
          {videos.map((v) => {
            const { role, name } = parseStaff(v.title ?? "");
            return (
              <button
                key={v.id}
                type="button"
                onClick={() => onPlay(v)}
                className="group relative aspect-video overflow-hidden rounded-sm bg-black/50 text-left"
              >
                <img
                  src={salonJobResolvedPosterUrl(v)}
                  alt={v.title ?? ""}
                  className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-transparent" />

                {/* Play button — center */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/95 text-black flex items-center justify-center text-base sm:text-lg group-hover:scale-110 transition shadow-2xl">
                    ▶
                  </span>
                </div>

                {/* Caption — bottom */}
                <div className="absolute bottom-0 inset-x-0 p-4 sm:p-5">
                  <div className="text-[9px] tracking-[0.3em] uppercase text-white/60 mb-1.5">
                    Interview
                  </div>
                  {role && (
                    <div className="text-[11px] text-white/70 font-light tracking-wide mb-0.5">
                      {role}
                    </div>
                  )}
                  <div className="font-serif text-base sm:text-lg lg:text-xl tracking-tight">{name}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// =====================================================================================
// Jobs
// =====================================================================================

function Jobs({
  jobs, support, onApply,
}: { jobs: Job[]; support: Job[]; onApply: (job: Job) => void }) {
  if (jobs.length === 0 && support.length === 0) return null;
  return (
    <section id="apply" className="py-16 sm:py-24 lg:py-32 px-5 sm:px-6 lg:px-12 bg-white">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <SectionLabel>Open Positions</SectionLabel>
          <h2 className="font-serif text-2xl sm:text-4xl lg:text-5xl mt-3 sm:mt-4 tracking-tight">募集中のポジション</h2>
        </div>

        <div className="space-y-4">
          {jobs.map((job, i) => (
            <JobCard key={job.id} job={job} index={i} onApply={onApply} />
          ))}
        </div>

        {support.length > 0 && (
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {support.map((job) => (
              <button
                key={job.id}
                type="button"
                onClick={() => onApply(job)}
                className="group p-6 border border-black/10 rounded-sm hover:border-black hover:bg-black hover:text-white transition flex items-center justify-between text-left"
              >
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase opacity-50 mb-1">
                    {job.position}
                  </div>
                  <div className="font-serif text-lg">{job.summary}</div>
                </div>
                <span aria-hidden className="text-2xl opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition">
                  →
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function JobCard({
  job, index, onApply,
}: { job: Job; index: number; onApply: (job: Job) => void }) {
  const [open, setOpen] = useState(index === 0);
  const title = job.position || job.summary || "募集要項";
  return (
    <article className="border border-black/10 rounded-sm overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full p-5 sm:p-8 text-left flex items-start justify-between gap-3 sm:gap-6 hover:bg-black/[0.02] transition"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap mb-3 sm:mb-4">
            {job.employmentCategoryLabel && (
              <EmploymentBadge label={job.employmentCategoryLabel} category={job.employmentCategory} />
            )}
            {job.startedOn && (
              <span className="text-[10px] sm:text-[11px] tracking-[0.2em] uppercase text-black/40 hidden sm:inline">
                掲載開始 {job.startedOn}
              </span>
            )}
          </div>
          <h3 className="font-serif text-xl sm:text-3xl tracking-tight">{title}</h3>
          {job.salary && (
            <div className="mt-2 sm:mt-3 text-xs sm:text-base text-black/70 font-light max-w-2xl line-clamp-2 sm:line-clamp-none">
              {job.salary.summary}
            </div>
          )}
        </div>
        <span
          aria-hidden
          className={`shrink-0 w-11 h-11 sm:w-10 sm:h-10 rounded-full border border-black/20 flex items-center justify-center transition-transform text-base ${
            open ? "rotate-45" : ""
          }`}
        >
          +
        </span>
      </button>

      {open && (
        <div className="px-5 sm:px-8 pb-6 sm:pb-10 grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 border-t border-black/5 pt-6 sm:pt-8">
          <div className="lg:col-span-3">
            <p className="text-sm sm:text-base leading-relaxed text-black/80 whitespace-pre-line font-light">
              {job.description}
            </p>
          </div>
          {job.salary && (
            <SpecBlock title="給与">
              <SpecRow label="給与" value={job.salary.summary} />
              {job.salary.bonus && <SpecRow label="賞与" value={job.salary.bonus} />}
              {job.salary.increase && <SpecRow label="昇給" value={job.salary.increase} />}
              {job.salary.primaryAllowance && <SpecRow label="諸手当" value={job.salary.primaryAllowance} />}
              {job.salary.otherAllowance && <SpecRow label="その他手当" value={job.salary.otherAllowance} />}
              {job.salary.supplement && <SpecRow label="補足" value={job.salary.supplement} />}
            </SpecBlock>
          )}
          {job.workingSchedule && (
            <SpecBlock title="勤務時間 / 休日">
              <SpecRow label="勤務時間" value={job.workingSchedule.workingHours} />
              {job.workingSchedule.holiday && <SpecRow label="休日" value={job.workingSchedule.holiday} />}
              {job.workingSchedule.vacation && <SpecRow label="休暇" value={job.workingSchedule.vacation} />}
              {job.workingSchedule.paidVacationDays != null && (
                <SpecRow label="有給" value={`${job.workingSchedule.paidVacationDays}日`} />
              )}
              {job.workingSchedule.totalHolidaysPerYear != null && (
                <SpecRow label="年間休日" value={`${job.workingSchedule.totalHolidaysPerYear}日`} />
              )}
            </SpecBlock>
          )}
          {(job.welfareBenefits || job.requirements) && (
            <SpecBlock title="福利厚生 / 応募資格">
              {job.welfareBenefits?.socialInsurance && (
                <SpecRow label="社会保険" value={job.welfareBenefits.socialInsurance} />
              )}
              {job.welfareBenefits?.other && <SpecRow label="その他" value={job.welfareBenefits.other} />}
              {job.welfareBenefits?.trainingForBeginners && (
                <SpecRow label="研修" value={job.welfareBenefits.trainingForBeginners} />
              )}
              {job.requirements?.qualifications && (
                <SpecRow label="必須資格" value={job.requirements.qualifications} />
              )}
              {job.requirements?.otherCondition && (
                <SpecRow label="その他条件" value={job.requirements.otherCondition} />
              )}
              {job.trialPeriod && <SpecRow label="試用期間" value={job.trialPeriod} />}
            </SpecBlock>
          )}
          <div className="lg:col-span-3 flex justify-end pt-4 border-t border-black/5">
            <button
              type="button"
              onClick={() => onApply(job)}
              className="inline-flex items-center gap-2 px-7 h-12 rounded-full bg-black text-white text-sm tracking-wide hover:bg-black/80 transition"
            >
              この求人に応募する
              <span aria-hidden>→</span>
            </button>
          </div>
        </div>
      )}
    </article>
  );
}

// Color-coded badge for employment category. Tied to the salon_jobs.employment_category_id enum.
// Real-DB values (sample): full_time(322) / freelance(118) / part_time(72) / contract(5)
const EMPLOYMENT_BADGE_STYLE: Record<string, string> = {
  full_time:         "bg-[#0a0a0a] text-white",                     // 正社員 — strongest
  freelance:         "bg-white text-[#0a0a0a] border border-black/30", // 業務委託
  part_time:         "bg-[#e8b66b] text-[#0a0a0a]",                 // パート — gold accent
  contract:          "bg-[#3a3a32] text-white",                     // 契約
  business_contract: "bg-white text-[#0a0a0a] border border-black/30", // alias of freelance
  trainee:           "bg-[#f0e6d2] text-[#0a0a0a]",                 // 研修
  intern:            "bg-[#f0e6d2] text-[#0a0a0a]",                 // インターン
};
function EmploymentBadge({ label, category }: { label: string; category: string | null }) {
  const cls = EMPLOYMENT_BADGE_STYLE[category ?? ""] ?? "bg-black text-white";
  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium tracking-wide ${cls}`}>
      {label}
    </span>
  );
}

function SpecBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-[10px] tracking-[0.3em] uppercase text-black/50 mb-4 pb-3 border-b border-black/10">
        {title}
      </h4>
      <dl className="space-y-3">{children}</dl>
    </div>
  );
}

function SpecRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-[10px] tracking-[0.2em] uppercase text-black/40 mb-1">{label}</dt>
      <dd className="text-sm text-black/80 leading-relaxed whitespace-pre-line font-light">{value}</dd>
    </div>
  );
}

// =====================================================================================
// Workstyle (icons grid)
// =====================================================================================

function Workstyle({ title, items }: { title: string; items: WorkstyleArticle[] }) {
  items = items.filter((it) => it.title || it.body);
  if (items.length === 0) return null;
  return (
    <section className="py-16 sm:py-24 lg:py-32 px-5 sm:px-6 lg:px-12 bg-[#fafaf7]">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <SectionLabel>Work Style</SectionLabel>
          <h2 className="font-serif text-2xl sm:text-4xl lg:text-5xl mt-3 sm:mt-4 tracking-tight">{title}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {items.map((it, i) => (
            <article
              key={i}
              className="bg-white rounded-sm overflow-hidden border border-black/5 group min-w-0"
            >
              <OptionalArticleImage
                articleId={it.articleId}
                imageUpdatedAt={it.imageUpdatedAt}
                alt={it.title}
                containerClassName="aspect-[4/3] overflow-hidden bg-black/5"
                className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
              />
              <div className="p-6 sm:p-8 min-w-0">
                <h3 className="font-serif text-lg sm:text-xl tracking-tight [overflow-wrap:break-word]">{it.title}</h3>
                {it.body && (
                  <p className="text-sm text-black/70 leading-relaxed font-light mt-3 whitespace-pre-line [word-break:keep-all] [overflow-wrap:break-word] [text-wrap:pretty]">
                    {it.body}
                  </p>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// =====================================================================================
// Concept (alternating image/text)
// =====================================================================================

function Concept({ title, items }: { title: string; items: ConceptArticle[] }) {
  if (items.length === 0) return null;
  return (
    <section className="py-16 sm:py-24 lg:py-32 px-5 sm:px-6 lg:px-12 bg-white">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-20">
          <SectionLabel>Concept</SectionLabel>
          <h2 className="font-serif text-2xl sm:text-4xl lg:text-5xl mt-3 sm:mt-4 tracking-tight">{title}</h2>
        </div>
        <div className="space-y-16 sm:space-y-24 lg:space-y-32">
          {items.map((it, i) => {
            const reverse = i % 2 === 1;
            return (
              <div
                key={i}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center ${
                  reverse ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="lg:col-span-7">
                  <OptionalArticleImage
                    articleId={it.articleId}
                    imageUpdatedAt={it.imageUpdatedAt}
                    alt={it.title}
                    containerClassName="overflow-hidden bg-black/5"
                    className="w-full h-auto object-contain"
                  />
                </div>
                <div className="lg:col-span-5">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-black/40 mb-4">
                    {String(i + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
                  </div>
                  <h3 className="font-serif text-2xl sm:text-3xl leading-snug tracking-tight mb-6">
                    {it.title}
                  </h3>
                  <p className="text-sm sm:text-base text-black/70 leading-relaxed whitespace-pre-line font-light">
                    {it.body}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// =====================================================================================
// Data (graphs)
// =====================================================================================

function Data({ title, graphs }: { title: string; graphs: Graph[] }) {
  const textGraphs = graphs.filter((g) => g.category === "text");
  const donutGraphs = graphs.filter((g) => g.category === "donut_chart");
  if (graphs.length === 0) return null;
  return (
    <section id="data" className="py-16 sm:py-24 lg:py-32 px-5 sm:px-6 lg:px-12 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a] to-[#1a1a18] text-white">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-12 sm:mb-20">
          <SectionLabel light>Data</SectionLabel>
          <h2 className="font-serif text-2xl sm:text-4xl lg:text-5xl mt-3 sm:mt-4 tracking-tight">{title}</h2>
          <p className="text-white/50 text-xs sm:text-sm mt-3 sm:mt-4 max-w-md mx-auto font-light">
            数字とグラフで知る、サロンのリアル。
          </p>
        </div>

        {textGraphs.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 mb-px">
            {textGraphs.map((g, i) => (
              <StatCard key={i} graph={g} index={i + 1} />
            ))}
          </div>
        )}

        {donutGraphs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
            {donutGraphs.map((g, i) => (
              <DonutCard key={i} graph={g} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function StatCard({ graph, index }: { graph: Graph; index: number }) {
  const value = salonjobsFormatGraphValue(graph.value, graph.unit);
  const mainText = value || graph.title || graph.body || "";
  const showBody = graph.body && (value || graph.title);
  return (
    <div className="bg-[#0a0a0a] p-8 sm:p-10 lg:p-12 flex flex-col items-start text-left relative overflow-hidden group">
      {/* subtle accent number */}
      <div className="absolute top-4 right-5 sm:top-6 sm:right-7 font-serif text-5xl sm:text-6xl text-white/[0.04] select-none">
        {String(index).padStart(2, "0")}
      </div>
      <div className="text-[10px] tracking-[0.35em] uppercase text-[#e8b66b] mb-6 sm:mb-8">
        Feature {index}
      </div>
      <div className="font-serif text-2xl sm:text-3xl lg:text-[2.25rem] leading-[1.3] tracking-tight relative z-10">
        {mainText}
      </div>
      {showBody && (
        <p className="text-xs sm:text-sm text-white/55 mt-4 sm:mt-5 font-light leading-relaxed relative z-10">
          {graph.body}
        </p>
      )}
      {/* hover line */}
      <div className="mt-6 sm:mt-8 w-8 h-px bg-white/30 group-hover:w-16 group-hover:bg-[#e8b66b] transition-all duration-500" />
    </div>
  );
}

// Soft warm palette for donut charts (gold → sand → ash)
const DONUT_COLORS = ["#e8b66b", "#d8a057", "#c08960", "#9a7058", "#6e5a4f", "#3f3a36"];

function DonutCard({ graph }: { graph: Graph }) {
  const data = useMemo(() => graph.values?.filter((v) => v.value > 0) ?? [], [graph.values]);
  const total = data.reduce((s, d) => s + d.value, 0);
  const top = data[0];
  const topPct = top && total > 0 ? Math.round((top.value / total) * 100) : 0;

  return (
    <div className="bg-[#0a0a0a] p-8 sm:p-10 flex flex-col">
      <div className="text-[10px] tracking-[0.35em] uppercase text-white/50 mb-6 sm:mb-8 text-center">
        {graph.title}
      </div>

      <div className="relative" style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="key"
              cx="50%"
              cy="50%"
              innerRadius={72}
              outerRadius={92}
              paddingAngle={2}
              cornerRadius={2}
              stroke="none"
              startAngle={90}
              endAngle={-270}
              isAnimationActive
              animationDuration={900}
              animationEasing="ease-out"
            >
              {data.map((_, i) => (
                <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        {/* center caption — biggest segment */}
        {top && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <div className="font-serif text-3xl sm:text-4xl tabular-nums tracking-tight text-white">
              {topPct}<span className="text-base sm:text-lg text-white/60 ml-0.5">%</span>
            </div>
            <div className="text-[9px] tracking-[0.25em] uppercase text-white/40 mt-1 truncate max-w-[10rem] text-center">
              {top.key}
            </div>
          </div>
        )}
      </div>

      <ul className="mt-6 space-y-2.5">
        {data.map((d, i) => {
          const pct = Math.round((d.value / total) * 100);
          return (
            <li key={i} className="text-[11px] text-white/80">
              <div className="flex items-baseline gap-3">
                <span
                  className="w-2 h-2 rounded-full shrink-0 self-center"
                  style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }}
                />
                <span className="flex-1 truncate font-light">{d.key}</span>
                <span className="tabular-nums text-white/90 font-medium">{pct}%</span>
              </div>
              <div className="mt-1.5 ml-5 h-px bg-white/5 overflow-hidden">
                <div
                  className="h-full transition-all duration-700"
                  style={{ width: `${pct}%`, backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length], opacity: 0.6 }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// =====================================================================================
// Visit (skill-up gallery)
// =====================================================================================

function Visit({ title, items }: { title: string; items: VisitArticle[] }) {
  if (items.length === 0) return null;
  return (
    <section className="py-16 sm:py-24 lg:py-32 px-5 sm:px-6 lg:px-12 bg-[#fafaf7]">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <SectionLabel>Skill Up</SectionLabel>
          <h2 className="font-serif text-2xl sm:text-4xl lg:text-5xl mt-3 sm:mt-4 tracking-tight">{title}</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it, i) => (
            <div key={i} className="group">
              <OptionalArticleImage
                articleId={it.articleId}
                imageUpdatedAt={it.imageUpdatedAt}
                alt={it.title}
                containerClassName="aspect-square overflow-hidden bg-black/5"
                className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
              />
              <div className="mt-3 text-xs sm:text-sm font-light tracking-tight">{it.title}</div>
              {it.body && <div className="text-[11px] text-black/50 mt-0.5 font-light">{it.body}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =====================================================================================
// Staff — padding wrapper only; heading lives inside the embed
// =====================================================================================

function Staff({ salon }: { salon: Salon }) {
  return (
    <section id="staff" className="py-16 sm:py-24 lg:py-32 px-5 sm:px-6 lg:px-12 bg-white">
      <div className="max-w-[1400px] mx-auto">
        <HairbookStaffEmbed salon={salon} />
      </div>
    </section>
  );
}

// =====================================================================================
// Process
// =====================================================================================

function Process({ title, items }: { title: string; items: ProcessArticle[] }) {
  if (items.length === 0) return null;
  return (
    <section className="py-16 sm:py-24 lg:py-32 px-5 sm:px-6 lg:px-12 bg-[#fafaf7]">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <SectionLabel>Hiring Process</SectionLabel>
          <h2 className="font-serif text-2xl sm:text-4xl lg:text-5xl mt-3 sm:mt-4 tracking-tight">{title}</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          {items.map((it, i) => (
            <div key={i} className="flex gap-6 sm:gap-10 pb-10 last:pb-0">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full border-2 border-black/20 flex items-center justify-center font-serif text-lg">
                  {String(it.step).padStart(2, "0")}
                </div>
                {i < items.length - 1 && <div className="w-px flex-1 bg-black/10 my-2" />}
              </div>
              <div className="pb-6 flex-1">
                <h3 className="font-serif text-xl sm:text-2xl tracking-tight">{it.title}</h3>
                <p className="text-sm text-black/70 leading-relaxed mt-3 font-light">{it.body}</p>
                {it.articleId && (
                  <OptionalProcessImage
                    articleId={it.articleId}
                    imageUpdatedAt={it.imageUpdatedAt}
                    alt={it.title}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OptionalProcessImage({
  articleId,
  imageUpdatedAt,
  alt,
}: {
  articleId: string;
  imageUpdatedAt?: string | null;
  alt: string | null;
}) {
  return (
    <OptionalArticleImage
      articleId={articleId}
      imageUpdatedAt={imageUpdatedAt}
      alt={alt}
      containerClassName="mt-5 max-w-[180px] bg-white border border-black/5 p-2"
      className="w-full h-auto object-contain"
    />
  );
}

function OptionalArticleImage({
  articleId, imageUpdatedAt, src, alt, className, containerClassName, onMissing,
}: {
  articleId?: string | null;
  imageUpdatedAt?: string | null;
  src?: string | null;
  alt: string | null;
  className: string;
  containerClassName?: string;
  onMissing?: () => void;
}) {
  const [hidden, setHidden] = useState(false);
  const imageUrl = src || (articleId ? salonjobsArticleImageUrl(articleId, "gallery", imageUpdatedAt) : null);
  if (!imageUrl || hidden) return null;
  const image = (
    <img
      src={imageUrl}
      alt={alt ?? ""}
      loading="lazy"
      className={className}
      onLoad={(event) => {
        if (event.currentTarget.currentSrc.includes("/assets/notfound")) {
          setHidden(true);
          onMissing?.();
        }
      }}
      onError={() => {
        setHidden(true);
        onMissing?.();
      }}
    />
  );
  if (!containerClassName) return image;
  return <div className={containerClassName}>{image}</div>;
}

// =====================================================================================
// Apply CTA
// =====================================================================================

function ApplyCta({ salon }: { salon: Salon; settings: Settings }) {
  return (
    <section id="contact" className="py-16 sm:py-24 lg:py-32 px-5 sm:px-6 lg:px-12 bg-[#0a0a0a] text-white">
      <div className="max-w-3xl mx-auto text-center">
        <SectionLabel light>Apply</SectionLabel>
        <h2 className="font-serif text-2xl sm:text-4xl lg:text-5xl mt-3 sm:mt-4 tracking-tight">
          一緒に働ける日を、<br className="hidden sm:block" />お待ちしています。
        </h2>
        <p className="text-white/70 mt-6 text-sm sm:text-base font-light max-w-md mx-auto">
          ご興味のあるポジションから、応募フォームよりご連絡ください。
        </p>
        <a
          href="#apply"
          data-controller="tracking"
          data-action="click->tracking#track"
          data-name="job_requirements_clicked"
          data-salon-id={String(salon.id)}
          data-location="apply_cta"
          className="mt-10 inline-flex items-center justify-center gap-2 px-8 h-14 rounded-full bg-white text-black text-sm tracking-wide hover:bg-white/90 transition"
        >
          募集要項を見る
          <span aria-hidden>↑</span>
        </a>
      </div>
    </section>
  );
}

// =====================================================================================
// Footer
// =====================================================================================

function Footer({ salon, company }: { salon: Salon; company: Company }) {
  return (
    <footer className="px-5 sm:px-6 lg:px-12 py-16 bg-[#0a0a0a] text-white border-t border-white/10">
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-10 text-sm">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3">Salon</div>
          <div className="font-serif text-base mb-2">{salon.name}</div>
          <div className="text-white/70 text-xs leading-relaxed font-light whitespace-pre-line">
            {salon.address}
          </div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3">Hours</div>
          <div className="text-white/70 text-xs leading-relaxed font-light whitespace-pre-line">
            {salon.hour}
          </div>
          <div className="text-white/70 text-xs mt-3 font-light">定休日: {salon.holiday}</div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3">Company</div>
          <div className="font-serif text-base mb-2">{company.name}</div>
          <div className="text-white/70 text-xs font-light">
            {company.location}
          </div>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto mt-12 pt-6 border-t border-white/10 flex items-center justify-between text-[10px] text-white/40 tracking-wider">
        <span>© {new Date().getFullYear()} {company.name}</span>
        <span>Powered by Hairbook Salonjobs</span>
      </div>
    </footer>
  );
}

// =====================================================================================
// Style Showcase — padding wrapper only; heading lives inside the embed
// =====================================================================================

function StyleShowcase({ salon }: { salon: Salon }) {
  return (
    <section id="styles" className="py-16 sm:py-24 lg:py-32 px-5 sm:px-6 lg:px-12 bg-white">
      <div className="max-w-[900px] mx-auto">
        <HairbookStylesEmbed salon={salon} />
      </div>
    </section>
  );
}

// =====================================================================================
// Instagram — padding wrapper only; heading lives inside the embed
// =====================================================================================

function Instagram({ salon }: { salon: Salon }) {
  if (!salon.instagramUrl) return null;
  return (
    <section id="instagram" className="py-16 sm:py-24 lg:py-32 px-5 sm:px-6 lg:px-12 bg-[#fafaf7]">
      <div className="max-w-[1100px] mx-auto">
        <HairbookInstagramEmbed salon={salon} />
      </div>
    </section>
  );
}

// =====================================================================================
// LINE — official account friend CTA
// =====================================================================================

function Line({ salon }: { salon: Salon }) {
  const url = salonjobsLineOfficialUrl(salon.lineOfficialId);
  if (!url) return null;
  return (
    <section id="line" className="py-16 sm:py-24 lg:py-32 px-5 sm:px-6 lg:px-12 bg-white">
      <div className="max-w-[900px] mx-auto text-center">
        <SectionLabel>LINE</SectionLabel>
        <h2 className="font-serif text-2xl sm:text-4xl lg:text-5xl mt-3 sm:mt-4 tracking-tight">LINEで相談</h2>
        <p className="mt-4 text-sm sm:text-base text-black/60">
          お仕事のご相談やサロンの最新情報は、公式 LINE からお気軽にどうぞ。
        </p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-controller="tracking"
          data-action="click->tracking#track"
          data-name="line_cta_clicked"
          data-salon-id={String(salon.id)}
          data-location="line_cta"
          className="mt-8 inline-flex items-center justify-center gap-2 px-8 h-12 rounded-full bg-[#06C755] text-white text-sm font-medium hover:bg-[#05b34c] transition-colors"
        >
          LINEはこちら
          <span aria-hidden>↗</span>
        </a>
      </div>
    </section>
  );
}

// =====================================================================================
// Customers — salon-side free input about the customer base
// =====================================================================================

function CustomerSection({ title, customers }: { title: string; customers: Customers }) {
  return (
    <section id="customers" className="py-16 sm:py-24 lg:py-32 px-5 sm:px-6 lg:px-12 bg-[#fafaf7]">
      <div className="max-w-[1100px] mx-auto">
        <div className="text-center mb-10 sm:mb-16">
          <SectionLabel>Customers</SectionLabel>
          <h2 className="font-serif text-2xl sm:text-4xl lg:text-5xl mt-3 sm:mt-4 tracking-tight">{title}</h2>
        </div>

        <div className="bg-white rounded-2xl p-8 sm:p-10 lg:p-14 border border-black/5">
          <p className="font-serif text-xl sm:text-2xl lg:text-3xl leading-[1.5] tracking-tight [word-break:keep-all] [overflow-wrap:break-word] [text-wrap:pretty]">
            {customers.headline}
          </p>
          {customers.subhead && (
            <p className="text-sm sm:text-base text-black/60 mt-3 sm:mt-4 font-light leading-relaxed [word-break:keep-all] [overflow-wrap:break-word] [text-wrap:pretty]">
              {customers.subhead}
            </p>
          )}

          {customers.demographics.length > 0 && (
            <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-px mt-10 sm:mt-12 bg-black/10 border-y border-black/10">
              {customers.demographics.map((d, i) => (
                <div key={i} className="bg-white p-4 sm:p-6 flex flex-col">
                  <dt className="text-[10px] tracking-[0.25em] uppercase text-black/40">{d.label}</dt>
                  <dd className="font-serif text-base sm:text-xl text-black/85 mt-2 leading-snug">{d.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {customers.voiceQuotes.length > 0 && (
            <div className="mt-10 sm:mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
              {customers.voiceQuotes.map((q, i) => (
                <blockquote key={i} className="relative">
                  <span className="absolute -top-4 -left-1 font-serif text-5xl text-black/15 select-none leading-none">"</span>
                  <p className="text-sm text-black/70 leading-relaxed font-light pl-4 [word-break:keep-all] [overflow-wrap:break-word] [text-wrap:pretty]">
                    {q}
                  </p>
                </blockquote>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// =====================================================================================
// RelatedLinks — padding wrapper only; heading lives inside the embed
// =====================================================================================

function RelatedLinks({ salon }: { salon: Salon }) {
  return (
    <section className="py-16 sm:py-20 px-5 sm:px-6 lg:px-12 bg-white border-t border-black/5">
      <div className="max-w-[1100px] mx-auto">
        <HairbookKnowMoreEmbed salon={salon} />
      </div>
    </section>
  );
}

// =====================================================================================
// Application Form Modal — opens from JobCard / support job buttons
// =====================================================================================

const GENDER_OPTIONS = [
  { value: "female", label: "女性" },
  { value: "male", label: "男性" },
  { value: "other", label: "その他" },
  { value: "no_answer", label: "回答しない" },
];

// 応募受付 Worker（worker/salonjobs-apply）。店舗の apply_mail_to 宛に Resend でメール送信する
const APPLY_ENDPOINT = "https://salonjobs-apply.long-glitter-7086.workers.dev";

function ApplicationFormModal({
  job, salon, onClose,
}: { job: Job; salon: Salon; onClose: () => void }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const jobTitle = job.position || job.summary || "募集要項";

  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handler);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handler);
    };
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const form = e.currentTarget;
      const formData = new FormData(form);
      const body = new URLSearchParams();
      formData.forEach((value, key) => body.set(key, value as string));
      const res = await fetch(APPLY_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSubmitted(true);
    } catch {
      alert("送信に失敗しました。もう一度お試しください。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-0 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-2xl sm:rounded-2xl overflow-hidden shadow-2xl my-0 sm:my-8 max-h-[100vh] sm:max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="応募フォーム"
      >
        {/* Header */}
        <div className="px-6 sm:px-10 pt-6 sm:pt-8 pb-4 sm:pb-6 border-b border-black/5 relative">
          <button
            type="button"
            onClick={onClose}
            aria-label="閉じる"
            className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 rounded-full hover:bg-black/5 flex items-center justify-center text-lg"
          >
            ✕
          </button>
          <div className="text-[10px] tracking-[0.3em] uppercase text-black/40">Apply</div>
          <div className="font-serif text-xl sm:text-2xl tracking-tight mt-1 pr-12">
            {salon.name.split("【")[0].trim()}
          </div>
          {jobTitle && (
            <div className="text-sm text-black/60 mt-2 font-light">
              {job.employmentCategoryLabel && (
                <span className="inline-block mr-2 text-[10px] tracking-[0.2em] uppercase border border-black/15 rounded-full px-2 py-0.5 align-middle">
                  {job.employmentCategoryLabel}
                </span>
              )}
              {jobTitle}
            </div>
          )}
        </div>

        {/* Body */}
        {submitted ? (
          <SuccessView onClose={onClose} />
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto px-6 sm:px-10 py-6 sm:py-8 space-y-5"
          >
            <input type="hidden" name="salonId" value={String(salon.id)} />
            <input type="hidden" name="jobId" value={String(job.id)} />
            <input type="hidden" name="サロン名" value={salon.name.split("【")[0].trim()} />
            <input type="hidden" name="応募ポジション" value={jobTitle} />
            {/* honeypot: 人間には見えない入力欄。埋まっていたら Worker 側で bot として破棄する */}
            <input
              type="text"
              name="homepage"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="absolute -left-[9999px] top-0 h-px w-px opacity-0"
            />
            {job.employmentCategoryLabel && (
              <input type="hidden" name="雇用形態" value={job.employmentCategoryLabel} />
            )}
            <input type="hidden" name="ページURL" value={salonjobsApplicationPageUrl(window.location.href, salon.id, job.id)} />
            <p className="text-xs text-black/50 font-light">
              全ての項目が必須です。送信内容はサロンに直接届きます。
            </p>

            {/* 名前 */}
            <FormRow label="お名前" required htmlFor="apply-name">
              <input
                id="apply-name"
                name="お名前"
                type="text"
                required
                autoComplete="name"
                placeholder="山田 花子"
                className="w-full h-12 px-4 border border-black/15 rounded-lg text-sm focus:outline-none focus:border-black"
              />
            </FormRow>

            {/* フリガナ */}
            <FormRow label="フリガナ" required htmlFor="apply-kana">
              <input
                id="apply-kana"
                name="フリガナ"
                type="text"
                required
                pattern="[ぁ-んァ-ヶー\s]+"
                title="ひらがな・カタカナで入力してください"
                placeholder="ヤマダ ハナコ"
                className="w-full h-12 px-4 border border-black/15 rounded-lg text-sm focus:outline-none focus:border-black"
              />
            </FormRow>

            {/* 性別 */}
            <FormRow label="性別" required>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {GENDER_OPTIONS.map((g) => (
                  <label
                    key={g.value}
                    className="cursor-pointer flex items-center justify-center h-11 px-3 border border-black/15 rounded-lg text-sm has-[:checked]:bg-black has-[:checked]:text-white has-[:checked]:border-black transition"
                  >
                    <input type="radio" name="性別" value={g.value} required className="sr-only" />
                    {g.label}
                  </label>
                ))}
              </div>
            </FormRow>

            {/* 電話番号 */}
            <FormRow label="電話番号" required htmlFor="apply-phone">
              <input
                id="apply-phone"
                name="電話番号"
                type="tel"
                required
                inputMode="tel"
                autoComplete="tel"
                pattern="[0-9+\s()\-]+"
                placeholder="090-1234-5678"
                className="w-full h-12 px-4 border border-black/15 rounded-lg text-sm focus:outline-none focus:border-black"
              />
            </FormRow>

            {/* メールアドレス */}
            <FormRow label="メールアドレス" required htmlFor="apply-email">
              <input
                id="apply-email"
                name="メールアドレス"
                type="email"
                required
                autoComplete="email"
                placeholder="example@example.com"
                className="w-full h-12 px-4 border border-black/15 rounded-lg text-sm focus:outline-none focus:border-black"
              />
            </FormRow>

            {/* 美容学校名 */}
            <FormRow label="美容学校名" required htmlFor="apply-school">
              <input
                id="apply-school"
                name="美容学校名"
                type="text"
                required
                placeholder="〇〇美容専門学校"
                className="w-full h-12 px-4 border border-black/15 rounded-lg text-sm focus:outline-none focus:border-black"
              />
            </FormRow>

            {/* 備考 */}
            <FormRow label="備考" required htmlFor="apply-memo">
              <textarea
                id="apply-memo"
                name="備考"
                required
                rows={4}
                placeholder="希望勤務時期、見学希望日、質問など何でもご記入ください。"
                className="w-full px-4 py-3 border border-black/15 rounded-lg text-sm focus:outline-none focus:border-black resize-y"
              />
            </FormRow>

            <div className="pt-2 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={onClose}
                className="sm:flex-1 inline-flex items-center justify-center h-12 rounded-full border border-black/15 text-sm tracking-wide hover:bg-black/[0.03] transition"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={submitting}
                data-controller="tracking"
                data-action="click->tracking#track"
                data-name="job_application_submitted"
                data-job-id={job.id}
                data-job-position={job.position}
                data-salon-id={String(salon.id)}
                className="sm:flex-[2] inline-flex items-center justify-center gap-2 h-12 rounded-full bg-black text-white text-sm tracking-wide hover:bg-black/85 transition disabled:opacity-60"
              >
                {submitting ? "送信中..." : (
                  <>
                    応募する
                    <span aria-hidden>→</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function FormRow({
  label, required, htmlFor, children,
}: { label: string; required?: boolean; htmlFor?: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-xs tracking-wide text-black/70 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

function SuccessView({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex-1 px-6 sm:px-10 py-12 sm:py-16 flex flex-col items-center justify-center text-center">
      <div className="w-14 h-14 rounded-full bg-black text-white flex items-center justify-center text-xl mb-6">✓</div>
      <h3 className="font-serif text-2xl sm:text-3xl tracking-tight">応募を受け付けました</h3>
      <p className="text-sm text-black/60 mt-4 font-light max-w-sm">
        ご応募ありがとうございます。<br />
        サロンより 1〜3 営業日以内にご連絡いたします。
      </p>
      <button
        type="button"
        onClick={onClose}
        className="mt-10 inline-flex items-center justify-center h-12 px-8 rounded-full bg-black text-white text-sm tracking-wide hover:bg-black/85 transition"
      >
        閉じる
      </button>
    </div>
  );
}

// =====================================================================================
// Shared bits
// =====================================================================================

function SectionLabel({ children, light }: { children: React.ReactNode; light?: boolean }) {
  return (
    <div className={`text-[10px] tracking-[0.4em] uppercase ${light ? "text-white/50" : "text-black/40"}`}>
      {children}
    </div>
  );
}
